import React from 'react';
import type { ImageSource, MarketingImage } from '../../assets/marketing/manifest';

interface PictureProps {
  image: MarketingImage;
  /** Empty for decorative images. Interactive wrappers should carry the name instead. */
  alt: string;
  /** The `sizes` attribute — how wide the image renders at each breakpoint. */
  sizes?: string;
  /** Above-the-fold: eager, sync decode, high fetch priority. */
  priority?: boolean;
  /**
   * Fill the positioned parent instead of reserving the intrinsic aspect
   * ratio. Use inside a box that already has a size (the hero frame).
   */
  fill?: boolean;
  className?: string;
  /** CSS object-position, from content/images.ts. */
  focal?: string;
  style?: React.CSSProperties;
}

const srcSet = (sources: readonly ImageSource[]) => sources.map((s) => `${s.src} ${s.w}w`).join(', ');

/**
 * Responsive image over the generated manifest: WebP with a JPEG fallback,
 * width descriptors for the browser to pick from, the intrinsic aspect ratio
 * reserved up front (zero layout shift) and the 16px LQIP painted behind it
 * while the real file loads. Everything it references is content-hashed into
 * /static/media/ and cached immutable.
 */
export default function Picture({
  image,
  alt,
  sizes = '100vw',
  priority = false,
  fill = false,
  className = '',
  focal,
  style,
}: PictureProps) {
  const fallback = image.jpg[image.jpg.length - 1];
  return (
    <picture
      className={`mk-picture ${fill ? 'mk-picture--fill' : ''} ${className}`.replace(/\s+/g, ' ').trim()}
      style={{
        ...(fill ? {} : { aspectRatio: `${image.width} / ${image.height}` }),
        backgroundImage: `url(${image.lqip})`,
        ...style,
      }}
    >
      <source type="image/webp" srcSet={srcSet(image.webp)} sizes={sizes} />
      <img
        src={fallback.src}
        srcSet={srcSet(image.jpg)}
        sizes={sizes}
        alt={alt}
        width={image.width}
        height={image.height}
        loading={priority ? 'eager' : 'lazy'}
        decoding={priority ? 'sync' : 'async'}
        style={focal ? { objectPosition: focal } : undefined}
        // React 18.2 does not know the camelCase prop; the lowercase attribute
        // reaches the DOM untouched and is what browsers read.
        {...({ fetchpriority: priority ? 'high' : 'auto' } as Record<string, string>)}
      />
    </picture>
  );
}
